import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Card,
  Button,
  Badge,
  Spinner,
  Textarea,
  makeStyles,
  tokens,
  Avatar,
  Divider,
} from '@fluentui/react-components'
import {
  ArrowLeft24Regular,
  Send24Regular,
  Attach24Regular,
  Person24Regular,
  Bot24Regular,
  Document24Regular,
  Image24Regular,
  Dismiss24Regular,
} from '@fluentui/react-icons'
import { api, TicketDetail as TicketDetailType, Message, Attachment } from '../lib/api'
import { useConfig } from '../context/ConfigContext'
import { ticketStatusColor, ticketStatusLabel } from '../lib/ticket-status'

const useStyles = makeStyles({
  root: {
    maxWidth: '800px',
    margin: '0 auto',
  },
  backButton: {
    marginBottom: '16px',
  },
  header: {
    marginBottom: '24px',
  },
  headerTop: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: '12px',
  },
  ticketNumber: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    marginBottom: '4px',
  },
  subject: {
    fontSize: '20px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    margin: 0,
  },
  meta: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
  },
  messagesSection: {
    marginBottom: '24px',
  },
  messageList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  message: {
    display: 'flex',
    gap: '12px',
  },
  messageClient: {
    flexDirection: 'row',
  },
  messageAgent: {
    flexDirection: 'row',
  },
  messageAvatar: {
    flexShrink: 0,
  },
  messageContent: {
    flex: 1,
    maxWidth: '85%',
  },
  messageBubble: {
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: '14px',
    lineHeight: '1.5',
  },
  messageBubbleClient: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground1,
  },
  messageBubbleAgent: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  messageTime: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    marginTop: '4px',
  },
  messageSender: {
    fontSize: '12px',
    fontWeight: '600',
    marginBottom: '4px',
    color: tokens.colorNeutralForeground2,
  },
  replySection: {
    position: 'sticky',
    bottom: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    paddingTop: '16px',
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  replyForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  replyActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  attachments: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  attachmentItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 10px',
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: '4px',
    fontSize: '12px',
  },
  removeAttachment: {
    cursor: 'pointer',
    color: tokens.colorNeutralForeground3,
    '&:hover': {
      color: tokens.colorPaletteRedForeground1,
    },
  },
  loadingState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px',
  },
  detailsCard: {
    padding: '16px',
    marginBottom: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  detailsTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    margin: 0,
  },
  detailRow: {
    display: 'flex',
    gap: '8px',
    fontSize: '13px',
  },
  detailLabel: {
    fontWeight: '600',
    color: tokens.colorNeutralForeground2,
    minWidth: '120px',
    flexShrink: 0,
  },
  detailValue: {
    color: tokens.colorNeutralForeground1,
    lineHeight: '1.4',
  },
  ticketTypeBadge: {
    fontSize: '11px',
  },
})

export function TicketDetail() {
  const styles = useStyles()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { config } = useConfig()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const [ticket, setTicket] = useState<TicketDetailType | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [loading, setLoading] = useState(true)
  const [replyContent, setReplyContent] = useState('')
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const [uploading, setUploading] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (id) {
      fetchTicket()
    }
    // fetchTicket is scoped to the current ticket id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const fetchTicket = async () => {
    if (!id) return
    
    setLoading(true)
    try {
      const result = await api.getTicket(id)
      setTicket(result.ticket)
      setMessages(result.messages)
    } catch (error) {
      console.error('Failed to fetch ticket:', error)
      setError('Failed to load ticket')
    } finally {
      setLoading(false)
    }
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    setUploading(true)
    setError(null)

    try {
      for (const file of Array.from(files)) {
        const result = await api.uploadFile(file)
        setAttachments(prev => [...prev, result.attachment])
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload file')
    } finally {
      setUploading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const removeAttachment = (attachmentId: string) => {
    setAttachments(prev => prev.filter(a => a.id !== attachmentId))
  }

  const handleSendReply = async () => {
    if (!id || !replyContent.trim()) return

    setSending(true)
    setError(null)

    try {
      const result = await api.addTicketMessage(id, {
        content: replyContent.trim(),
        attachmentIds: attachments.map(a => a.id),
      })

      setMessages(prev => [...prev, result.message])
      setReplyContent('')
      setAttachments([])
      
      // Refresh ticket to get updated status
      fetchTicket()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message')
    } finally {
      setSending(false)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getFileIcon = (contentType: string) => {
    if (contentType.startsWith('image/')) {
      return <Image24Regular />
    }
    return <Document24Regular />
  }

  if (loading) {
    return (
      <div className={styles.root}>
        <div className={styles.loadingState}>
          <Spinner size="large" label="Loading ticket..." />
        </div>
      </div>
    )
  }

  if (!ticket) {
    return (
      <div className={styles.root}>
        <Button
          appearance="subtle"
          icon={<ArrowLeft24Regular />}
          onClick={() => navigate('/tickets')}
          className={styles.backButton}
        >
          Back to My Tickets
        </Button>
        <Card>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <h3>Ticket not found</h3>
            <p>The ticket you are looking for does not exist or you do not have access to it.</p>
          </div>
        </Card>
      </div>
    )
  }

  const status = {
    label: ticket.statusLabel || ticketStatusLabel(ticket.status, config?.statusLabels),
    color: ticketStatusColor(ticket.status),
  }
  return (
    <div className={styles.root}>
      <Button
        appearance="subtle"
        icon={<ArrowLeft24Regular />}
        onClick={() => navigate('/tickets')}
        className={styles.backButton}
      >
        Back to My Tickets
      </Button>

      <div className={styles.header}>
        <div className={styles.headerTop}>
          <div>
            <p className={styles.ticketNumber}>
              {ticket.ticketNumber}
              {ticket.ticketType === 'it_request' && (
                <Badge
                  appearance="tint"
                  color="brand"
                  size="small"
                  className={styles.ticketTypeBadge}
                  style={{ marginLeft: '8px' }}
                >
                  IT Request
                </Badge>
              )}
            </p>
            <h1 className={styles.subject}>{ticket.subject}</h1>
          </div>
          <Badge color={status.color} appearance="filled" size="large">
            {status.label}
          </Badge>
        </div>
        <div className={styles.meta}>
          <span>Created {formatDate(ticket.createdAt)}</span>
          {ticket.categoryName && <span>Category: {ticket.categoryName}</span>}
          {ticket.subcategoryName && <span>Subcategory: {ticket.subcategoryName}</span>}
          {ticket.tags.length > 0 && (
            <span>Tags: {ticket.tags.join(', ')}</span>
          )}
        </div>
      </div>

      {/* IT Request Details */}
      {ticket.ticketType === 'it_request' && (
        <Card className={styles.detailsCard}>
          <h4 className={styles.detailsTitle}>IT Request Details</h4>
          {ticket.requestItems && (
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Items Requested:</span>
              <span className={styles.detailValue}>{ticket.requestItems}</span>
            </div>
          )}
          {ticket.businessJustification && (
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Justification:</span>
              <span className={styles.detailValue}>{ticket.businessJustification}</span>
            </div>
          )}
          {ticket.managerEmail && (
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Manager:</span>
              <span className={styles.detailValue}>
                {ticket.managerName ? `${ticket.managerName} (${ticket.managerEmail})` : ticket.managerEmail}
              </span>
            </div>
          )}
        </Card>
      )}

      <Divider />

      <div className={styles.messagesSection}>
        <div className={styles.messageList}>
          {messages.map(message => {
            const isClient = message.senderType === 'client'
            
            return (
              <div
                key={message.id}
                className={`${styles.message} ${isClient ? styles.messageClient : styles.messageAgent}`}
              >
                <Avatar
                  className={styles.messageAvatar}
                  icon={isClient ? <Person24Regular /> : <Bot24Regular />}
                  color={isClient ? 'neutral' : 'brand'}
                  size={32}
                />
                <div className={styles.messageContent}>
                  <p className={styles.messageSender}>
                    {isClient ? 'You' : 'Support Team'}
                  </p>
                  <div
                    className={`${styles.messageBubble} ${
                      isClient ? styles.messageBubbleClient : styles.messageBubbleAgent
                    }`}
                  >
                    {message.content}
                  </div>
                  <p className={styles.messageTime}>{formatDate(message.createdAt)}</p>
                </div>
              </div>
            )
          })}
          <div ref={messagesEndRef} />
        </div>
      </div>

      <div className={styles.replySection}>
        <div className={styles.replyForm}>
          <Textarea
            value={replyContent}
            onChange={(_, data) => setReplyContent(data.value)}
            placeholder="Type your reply..."
            rows={3}
            disabled={sending}
            resize="vertical"
          />

          {attachments.length > 0 && (
            <div className={styles.attachments}>
              {attachments.map(attachment => (
                <div key={attachment.id} className={styles.attachmentItem}>
                  {getFileIcon(attachment.contentType)}
                  <span>{attachment.filename}</span>
                  <Dismiss24Regular
                    className={styles.removeAttachment}
                    onClick={() => removeAttachment(attachment.id)}
                  />
                </div>
              ))}
            </div>
          )}

          {error && (
            <Badge color="danger" appearance="filled">
              {error}
            </Badge>
          )}

          <div className={styles.replyActions}>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <Button
              appearance="subtle"
              icon={uploading ? <Spinner size="tiny" /> : <Attach24Regular />}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || sending}
            >
              Attach
            </Button>
            <Button
              appearance="primary"
              icon={sending ? <Spinner size="tiny" /> : <Send24Regular />}
              onClick={handleSendReply}
              disabled={!replyContent.trim() || sending}
            >
              {sending ? 'Sending...' : 'Send Reply'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
